import {
  DateFormats,
  DescriptionList,
  formatISODate,
  formatNumber,
  Modal,
  type ModalProps,
} from '@omnipresentgroup/design-system';
import { appendSymbol, toSentenceCase } from '@omnipresentgroup/helpers';
import {
  type Benefit,
  type BenefitPensionAmount,
  isBenefitPensionPercentage,
} from '@omnipresentgroup/types';
import { useMemo } from 'react';

import { BenefitCountry } from './BenefitCountry';
import { BenefitPackageTag } from './BenefitPackageTag';
import { EmployerCost } from './EmployerCost';

const MODAL_TITLE = 'Benefit details';

type Props = {
  modalProps: Omit<ModalProps, 'children'>;
  handleClose: () => void;
  benefit: Benefit;
};

const appendPercentageSymbol = (value: string | undefined) =>
  appendSymbol(value, '%', '-');

const getPensionAmountItems = (pensionDetails: Benefit['pensionDetails']) => {
  if (!pensionDetails) {
    return [];
  }
  if (isBenefitPensionPercentage(pensionDetails)) {
    return [];
  }
  const { employeeAmount, employerAmount } =
    pensionDetails as BenefitPensionAmount;

  return [
    ...(employeeAmount
      ? [
          {
            header: 'Employee contribution',
            detail: formatNumber({
              input: Number(employeeAmount.amount) || 0,
              currency: employeeAmount.currency,
            }),
          },
        ]
      : []),
    ...(employerAmount
      ? [
          {
            header: 'Employer contribution',
            detail: formatNumber({
              input: Number(employerAmount.amount) || 0,
              currency: employerAmount.currency,
            }),
          },
        ]
      : []),
    ...(pensionDetails?.employeeVoluntaryContributionAmount
      ? [
          {
            header: 'Voluntary contribution',
            detail: formatNumber({
              input:
                Number(
                  pensionDetails.employeeVoluntaryContributionAmount.amount,
                ) || 0,
              currency:
                pensionDetails.employeeVoluntaryContributionAmount.currency,
            }),
          },
        ]
      : []),
  ];
};

const getPensionPercentageItems = (
  pensionDetails: Benefit['pensionDetails'],
) => {
  if (!pensionDetails) {
    return [];
  }
  if (!isBenefitPensionPercentage(pensionDetails)) {
    return [];
  }
  const { employeePercentage, employerPercentage } = pensionDetails;

  return [
    ...(employeePercentage
      ? [
          {
            header: 'Employee contribution %',
            detail: appendPercentageSymbol(employeePercentage),
          },
        ]
      : []),
    ...(employerPercentage
      ? [
          {
            header: 'Employer contribution %',
            detail: appendPercentageSymbol(employerPercentage),
          },
        ]
      : []),
    ...(pensionDetails.employeeVoluntaryContributionPercentage
      ? [
          {
            header: 'Voluntary contribution %',
            detail: appendPercentageSymbol(
              pensionDetails.employeeVoluntaryContributionPercentage,
            ),
          },
        ]
      : []),
  ];
};

export const BenefitDetailsModal = ({
  modalProps,
  handleClose,
  benefit,
}: Props) => {
  const benefitDetails = useMemo(
    () => [
      {
        header: 'Employee',
        detail: benefit.employeeName ?? '-',
      },
      {
        header: 'Type',
        detail: toSentenceCase(benefit.benefitType) ?? '-',
      },
      {
        header: 'Package',
        detail: benefit.package ? <BenefitPackageTag benefit={benefit} /> : '-',
      },
      {
        header: 'Vendor',
        detail: benefit.vendor ?? '-',
      },
      {
        header: 'Mandatory',
        detail: benefit.mandatory ? 'Yes' : 'No',
      },
      {
        header: 'Effective date',
        detail: formatISODate(benefit.benefitEffectiveDate, DateFormats.Date),
      },
      {
        header: 'Employer cost',
        detail: <EmployerCost benefit={benefit} />,
      },
      {
        header: 'Country',
        detail: <BenefitCountry benefit={benefit} />,
      },
      {
        header: 'Coverage',
        detail: benefit.coverage ?? '-',
      },
      {
        header: 'Salary deduction',
        detail: formatNumber({
          input: Number(benefit.employeeDeduction?.amount || 0),
          currency: benefit.employeeDeduction?.currency,
        }),
      },
      {
        header: 'Dependents',
        detail: String(benefit.numberOfDependents),
      },
      ...getPensionAmountItems(benefit.pensionDetails),
      ...getPensionPercentageItems(benefit.pensionDetails),
    ],
    [benefit],
  );

  return (
    <Modal {...modalProps} span={6}>
      <Modal.Header title={MODAL_TITLE} onClose={handleClose} />
      <Modal.Body>
        <DescriptionList
          fraction="1/2"
          id="company-details-list"
          list={benefitDetails}
        />
      </Modal.Body>
    </Modal>
  );
};
